import { Alert, AlertIcon, Box, Button, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ContributingEntityStatus,
  useReopenContributingEntityResponseMutation,
  type ContributingEntityDetailDto,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useConfirmationModal } from "../../../../common/useConfirmationModal";
import { ContributingEntityStatusTag } from "../bid/contributing/ContributingEntityStatusTag";
import { emptyEspd } from "../bid/espd/useEspdAnswers";
import { ContributionValidationIssues } from "./ContributionValidationIssues";
import { ContributionWizard } from "./ContributionWizard";
import { useContributionUrls } from "./useContributionUrls";

interface Props {
  bidId: string;
  entityId: string;
  detail: ContributingEntityDetailDto;
}

export const ContributingEntityResponseView = ({ bidId, entityId, detail }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayer = useApiError();
  const confirmation = useConfirmationModal();
  const contributionUrls = useContributionUrls();
  const [reopenResponse, { isLoading: isReopening }] = useReopenContributingEntityResponseMutation();

  const backTo = contributionUrls.bidSupplierStep(detail.entity.eventId, bidId);
  const isComplete = detail.entity.status === ContributingEntityStatus.ResponseValidated;

  const askToReopen = () =>
    confirmation.show({
      title: t("Reopen for changes"),
      description: t(
        "{{name}} can edit and submit the answer again. No email is sent, so send a reminder to let them know.",
        { name: detail.entity.name },
      ),
      confirmButtonText: t("Reopen for changes") ?? undefined,
      onConfirmed: async () => {
        try {
          await reopenResponse({ bidId, entityId }).unwrap();
          navigate(backTo);
        } catch (e) {
          displayer.trigger(e);
        }
      },
    });

  return (
    <>
      <ContributionWizard
        detail={detail}
        espd={detail.espdResponse ?? emptyEspd()}
        qualificationResponses={detail.qualificationResponses ?? []}
        commitment={detail.commitment ?? { confirmed: false }}
        readOnly
        audience="lead"
        stepPaths={contributionUrls.reviewSteps(bidId, entityId)}
        backTo={backTo}
        backLabel={t("Back to the offer")}
        title={detail.entity.name}
        headingAside={<ContributingEntityStatusTag status={detail.entity.status} />}
        underSubtitle={
          <Stack spacing="3" mt="4">
            <Text fontSize="sm" color="smMuted">
              {t("This is the answer as the company submitted it. You cannot change it here.")}
            </Text>

            {isComplete ? (
              <Alert status="success" borderRadius="lg">
                <AlertIcon />
                <Text fontSize="sm">
                  {t("{{name}} has answered, and the answer is complete.", { name: detail.entity.name })}
                </Text>
              </Alert>
            ) : (
              <ContributionValidationIssues
                issues={detail.entity.validationIssues ?? []}
                audience="lead"
              />
            )}

            {detail.responseReopenable && (
              <Box>
                <Button variant="outline" size="sm" onClick={askToReopen} isLoading={isReopening}>
                  {t("Reopen for changes")}
                </Button>
                <Text fontSize="sm" color="smMuted" mt="2">
                  {t("Reopening lets the company correct the answer. It must then be submitted again.")}
                </Text>
              </Box>
            )}
          </Stack>
        }
      />
      {confirmation.modal}
    </>
  );
};
